<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-chart-bar"></i>
        {{ $t("fleetReports.volume_title") }}
      </h4>
    </div>

    <!-- Snapshot only: the backend has no date parameter — it always returns
         the CURRENT pending volume, so no date picker (it would be a lie). -->
    <div class="box">
      <div class="columns">
        <div class="column is-8" :style="{display: 'flex', alignItems: 'center'}">
          <span class="live-chip"><i class="fal fa-signal-stream"></i> {{ $t("fleetReports.live_data") }}</span>
          <span v-if="dashboardDate" :style="{color: 'grey', fontSize: '13px', marginLeft: '10px'}">
            {{ $t("fleetReports.date_value", {date: FleetFormatDate(dashboardDate)}) }}
          </span>
          <span v-if="measuredAt" :style="{color: 'grey', fontSize: '13px', marginLeft: '10px'}">
            {{ $t("fleetReports.updated_at", {time: FleetFormatTime(measuredAt)}) }}<template v-if="refreshing"> · {{ $t("fleetReports.refreshing") }}</template>
          </span>
        </div>
        <div class="column is-4" :style="{textAlign: 'right'}">
          <vs-button circle icon flat :title="$t('fleetReports.reload')" @click="FetchDashboard()">
            <i class="fal fa-sync"></i>
          </vs-button>
        </div>
      </div>
    </div>

    <Loading v-if="isLoadingDashboard"/>

    <div v-if="!isLoadingDashboard">
      <div class="box route-card" v-for="(route, index) in routesData" :key="index">
        <div class="route-card-head">
          <span>
            <span :style="{color: 'crimson', fontWeight: 'bold'}" v-if="route.route_code">
              {{ route.route_code }}
            </span>
            <span :style="{color: '#195BFF', fontWeight: 'bold', marginLeft: '6px'}">
              {{ route.route_name }}
            </span>
          </span>
          <span v-if="route.recommendation">
            <span class="recommend-chip" v-if="route.recommendation.size_class">
              {{ $t("fleetReports.recommended", {size: FleetSizeClassLabel(route.recommendation.size_class)}) }}
              <span v-if="route.recommendation.utilization_pct !== null">
                ({{ route.recommendation.utilization_pct }}%)
              </span>
            </span>
            <span class="recommend-chip none" v-else>{{ $t("fleetReports.no_fitting_truck") }}</span>
          </span>
        </div>

        <table class="volume-table">
          <thead>
          <tr>
            <th>#</th>
            <th>{{ $t("fleetReports.stop") }}</th>
            <th>{{ $t("fleetReports.bills") }}</th>
            <th>{{ $t("fleetReports.pieces") }}</th>
            <th>{{ $t("fleetReports.weight_kg") }}</th>
            <th>m³</th>
          </tr>
          </thead>
          <tbody>
          <template v-for="(stop, sIndex) in route.stops">
            <tr :key="'pending-' + sIndex">
              <td>{{ stop.sequence }}</td>
              <td>{{ stop.stop_name || (FleetStopTypeLabel(stop.stop_type) + ' #' + stop.stop_id) }}</td>
              <td>{{ stop.pending ? stop.pending.bills : 0 }}</td>
              <td>{{ stop.pending ? stop.pending.pieces : 0 }}</td>
              <td>{{ FleetFormatNumber(stop.pending ? stop.pending.weight_kg : 0, 1) }}</td>
              <td>{{ stop.pending ? stop.pending.cbm : 0 }}</td>
            </tr>
            <tr :key="'off-route-' + sIndex" v-if="FleetHasOffRouteVolume(stop.pending_off_route)"
                class="off-route-row">
              <td></td>
              <td>└ {{ $t("fleetReports.off_route") }}</td>
              <td>{{ stop.pending_off_route.bills }}</td>
              <td>{{ stop.pending_off_route.pieces }}</td>
              <td>{{ FleetFormatNumber(stop.pending_off_route.weight_kg, 1) }}</td>
              <td>{{ stop.pending_off_route.cbm }}</td>
            </tr>
          </template>
          </tbody>
          <tfoot v-if="route.totals">
          <tr>
            <td colspan="2" :style="{fontWeight: 'bold'}">{{ $t("fleetReports.total") }}</td>
            <td :style="{fontWeight: 'bold'}">{{ route.totals.bills }}</td>
            <td :style="{fontWeight: 'bold'}">{{ route.totals.pieces }}</td>
            <td :style="{fontWeight: 'bold'}">{{ FleetFormatNumber(route.totals.weight_kg, 1) }}</td>
            <td :style="{fontWeight: 'bold'}">{{ route.totals.cbm }}</td>
          </tr>
          <tr v-if="FleetHasOffRouteVolume(route.totals_off_route)" class="off-route-row">
            <td colspan="2" :style="{fontWeight: 'bold'}">{{ $t("fleetReports.off_route") }}</td>
            <td :style="{fontWeight: 'bold'}">{{ route.totals_off_route.bills }}</td>
            <td :style="{fontWeight: 'bold'}">{{ route.totals_off_route.pieces }}</td>
            <td :style="{fontWeight: 'bold'}">{{ FleetFormatNumber(route.totals_off_route.weight_kg, 1) }}</td>
            <td :style="{fontWeight: 'bold'}">{{ route.totals_off_route.cbm }}</td>
          </tr>
          </tfoot>
        </table>
      </div>

      <p v-if="!routesData.length" :style="{textAlign: 'center', color: 'grey', padding: '20px'}">
        {{ $t("fleetReports.no_route_data") }}
      </p>
    </div>
  </div>
</template>

<script>
import Loading from '@views/Loading';
import MixinFleetTrips from '@/Mixins/Fleet/Trips';

export default {
  components: {
    Loading,
  },
  mixins: [MixinFleetTrips],
  data: () => ({
    dashboardDate: '',   // the snapshot's own date, from the response
    measuredAt: '',      // when the backend measured the figures (it caches them)
    refreshing: false,   // stale figures shown while the backend refreshes them
    refetchTimer: null,
    routesData: [],
    isLoadingDashboard: false,
  }),
  methods: {
    // Stale figures come back at once (is_stale) while the backend refreshes
    // them in the background — fetch ONCE more, quietly, a little later.
    FetchDashboard(silent = false) {
      clearTimeout(this.refetchTimer);
      if (!silent) this.isLoadingDashboard = true;
      this.FetchVolumeDashboard().then((res) => {
        const data = res.data.data || {};
        this.routesData = data.routes || [];
        this.dashboardDate = data.date || '';
        this.measuredAt = data.as_of || '';
        this.isLoadingDashboard = false;
        this.refreshing = !silent && !!data.is_stale;
        // A reply landing after the page closed must not schedule a refetch.
        if (this.refreshing && !this.pageClosed) {
          this.refetchTimer = setTimeout(() => this.FetchDashboard(true), 15000);
        }
      }).catch(() => {
        if (!silent) this.routesData = [];
        this.isLoadingDashboard = false;
        this.refreshing = false;
      });
    },
  },
  created() {
    this.FetchDashboard();
  },
  beforeDestroy() {
    this.pageClosed = true;
    clearTimeout(this.refetchTimer);
  },
};
</script>

<style lang="scss" scoped>
.route-card {
  margin-bottom: 15px;
}

.route-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.recommend-chip {
  display: inline-block;
  background: #2dcc70;
  color: white;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 13px;
  font-weight: bold;

  &.none {
    background: crimson;
  }
}

.live-chip {
  display: inline-block;
  background: #2dcc70;
  color: white;
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 13px;
  font-weight: bold;
}

.volume-table {
  width: 100%;
  font-size: 13px;
  border-collapse: collapse;

  th, td {
    padding: 5px 8px;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
  }

  th {
    color: #666;
  }

  .off-route-row td {
    color: #e8a10f;
  }
}
</style>
